/**
 * RoomPage — composition root for the watch-room experience.
 *
 * Two-phase mount:
 *  1. RoomPage ensures a session and joins the room via REST (POST
 *     /api/rooms/:id/join), which persists membership and mints the sr_media
 *     cookie required for /hls access (WS-4). It also fetches the full
 *     MediaDetail (the create response omits it).
 *  2. Once joined, <RoomStage> mounts and binds the socket sync engine
 *     (useRoomSync, WS-8), the Video.js player (WS-6), the JoinPlaybackOverlay
 *     (WS-7), and the RoomSidebar (WS-5).
 *
 * WHY split phases: useRoomSync emits `room:join` on mount, and the server
 * asserts room membership on that event. For a direct-navigation viewer the
 * REST join must complete first, so the sync stage is mounted only after the
 * join resolves. Hooks can't be called conditionally, so the stage is a child.
 *
 * Per §5.3 this is one of two composition roots; it imports modules owned by
 * WS-6/7/8 against the frozen @syncreel/shared contracts. Those imports are
 * unresolved until those workstreams land — expected and correct; do not stub.
 */
import { useEffect, useRef, useState } from 'react';
import { Navigate, useParams, useLocation } from 'react-router-dom';
import type { Player } from '../player/useVideoJs';
import type {
  Member,
  RoomSummary,
  Role,
  MediaDetail,
  RoomStatePayload,
  RoomMembersPayload,
  RoomHostChangedPayload,
  RoomKickedPayload,
  SocketErrorPayload,
  RoomSubtitleAddedPayload,
} from '@syncreel/shared';
import { SYNC } from '@syncreel/shared';

// WHY: these three imports are owned by other workstreams and will not resolve
// until WS-6/7/8 land. They are typed via @syncreel/shared, so composition
// compiles as soon as each module is delivered. See DONE-WS-5.md.
import { VideoPlayer, type VideoPlayerHandle } from '../player/VideoPlayer';
import { useRoomSync } from '../hooks/useRoomSync';
import { useMediaCookie } from '../hooks/useMediaCookie';
import { JoinPlaybackOverlay } from '../player/controls/JoinPlaybackOverlay';

import { RoomSidebar } from '../components/RoomSidebar';
import { Spinner } from '../components/ui/Spinner';
import { Wordmark } from '../components/Wordmark';
import { Badge } from '../components/ui/Badge';
import { useToast } from '../components/ui/Toast';
import { getSocket } from '../socket/socket';
import { postJoinRoom } from '../api/client';
import { useSessionStore } from '../store/session';
import { logger } from '../api/logger';
import styles from './RoomPage.module.css';

/** Props for the inline connection/drift status pill. */
interface ConnectionStatusProps {
  connected: boolean;
  drift: number;
}

/** Small status pill showing sync health; driven by useRoomSync state. */
function ConnectionStatus({ connected, drift }: ConnectionStatusProps) {
  if (!connected) {
    return <Badge variant="warn" label="Reconnecting…" />;
  }
  const absDrift = Math.abs(drift);
  // WHY: SOFT_SYNC_MAX_S is the soft-sync threshold; below it we are "in sync".
  if (absDrift <= SYNC.SOFT_SYNC_MAX_S) {
    return <Badge variant="ok" label="In sync" />;
  }
  return <Badge variant="warn" label={`Adjusting ${drift > 0 ? '+' : '−'}${absDrift.toFixed(1)}s`} />;
}

/** Props for the joined stage (mounted only after REST join succeeds). */
interface RoomStageProps {
  room: RoomSummary;
  role: Role;
  mediaDetail: MediaDetail;
  joinCode: string | null;
  hostKey: string | null;
}

/**
 * Add a remote subtitle track to a Video.js player.
 * Used for host-uploaded (server-stored) custom subtitles broadcast via
 * `room:subtitle-added`. The URL is built from the mediaId + playlistUrl,
 * matching the path the HLS static router serves.
 */
function addSubtitleTrack(player: Player, mediaId: string, track: RoomSubtitleAddedPayload): void {
  const src = `/hls/${mediaId}/${track.playlistUrl}`;
  // WHY: the room:subtitle-added listener may fire twice if the effect re-runs
  // (e.g. currentRole changes after room:state). Skip tracks already added to
  // avoid duplicates in the captions menu.
  const existing = player.remoteTextTracks() as unknown as {
    length: number;
    [index: number]: { label: string | undefined; kind: string };
  };
  for (let i = 0; i < existing.length; i++) {
    const t = existing[i];
    if (t && t.label === track.label && t.kind === 'subtitles') return;
  }
  try {
    (player as unknown as {
      addRemoteTextTrack(o: {
        kind: string;
        src: string;
        label?: string;
        language?: string;
      }): unknown;
    }).addRemoteTextTrack({
      kind: 'subtitles',
      src,
      label: track.label,
      language: track.language,
    });
  } catch (err) {
    logger.warn('Failed to add custom subtitle track', { src, err });
  }
}

/**
 * RoomStage — binds the socket sync engine and renders the player + sidebar.
 * Mounted only after the REST join completes, so the socket `room:join` fires
 * against an established membership.
 */
function RoomStage({ room, role, mediaDetail, joinCode, hostKey }: RoomStageProps) {
  const toast = useToast();
  const playerHandle = useRef<VideoPlayerHandle>(null);
  const [player, setPlayer] = useState<Player | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [currentRole, setCurrentRole] = useState<Role>(role);
  const setRoom = useSessionStore((s) => s.setRoom);
  const clearRoom = useSessionStore((s) => s.clearRoom);
  const location = useLocation();
  const claimHost = useRef((location.state as { claimHost?: boolean } | null)?.claimHost === true);
  // WHY: custom subtitle tracks may arrive via room:subtitle-added before the
  // player is ready. Queue them and flush once the player reports ready.
  const pendingSubsRef = useRef<RoomSubtitleAddedPayload[]>([]);
  // WHY: the room container is the fullscreen target — when fullscreen+chat
  // is active, the whole layout (video + floating chat) fills the screen.
  const roomRef = useRef<HTMLDivElement>(null);
  const [isFsChat, setIsFsChat] = useState(false);
  // WHY: in fullscreen+chat mode the floating sidebar can be collapsed to
  // just a tab so the video is unobstructed. Toggled by clicking the tab.
  const [chatCollapsed, setChatCollapsed] = useState(false);

  // Subscribe to socket membership + error events for the sidebar and toasts.
  // The sync engine (useRoomSync) owns playback events; this only mirrors
  // membership and surfaces server error events as toasts.
  useEffect(() => {
    const sock = getSocket(room.id);
    const onState = (p: RoomStatePayload) => {
      setMembers(p.members);
      // WHY: the admin lobby's "Take over" flow sets claimHost; claim once, on
      // the first room:state, when the server-side room is known to exist.
      if (claimHost.current && hostKey && p.role !== 'host') {
        claimHost.current = false;
        sock.emit('room:claim-host', { roomId: room.id, hostKey });
      }
      // WHY: the server sends room:state with the caller's role after a
      // host claim; update the local role so the UI switches to host mode.
      if (p.role !== currentRole) {
        setCurrentRole(p.role);
        setRoom({ room, role: p.role, mediaDetail, joinCode, hostKey });
      }
    };
    const onMembers = (p: RoomMembersPayload) => setMembers(p.members);
    const onHostChanged = (p: RoomHostChangedPayload) => {
      // WHY: update member roles in the local list when the host changes.
      setMembers((prev) =>
        prev.map((m) => ({
          ...m,
          role: m.userId === p.hostUserId ? 'host' : 'viewer' as const,
        })),
      );
    };
    const onError = (p: SocketErrorPayload) => {
      logger.warn('socket error', p);
      toast.error(p.message);
    };
    const onKicked = (p: RoomKickedPayload) => {
      toast.error(`You were kicked: ${p.reason}`);
      // WHY: navigate back to the lobby after a short delay so the toast is visible.
      setTimeout(() => {
        clearRoom();
        window.location.href = '/';
      }, 1500);
    };
    const onSubtitleAdded = (p: RoomSubtitleAddedPayload) => {
      const pNow = playerHandle.current?.player();
      if (pNow) {
        addSubtitleTrack(pNow, mediaDetail.id, p);
      } else {
        pendingSubsRef.current.push(p);
      }
    };
    sock.on('room:state', onState);
    sock.on('room:members', onMembers);
    sock.on('room:host-changed', onHostChanged);
    sock.on('room:kicked', onKicked);
    sock.on('room:subtitle-added', onSubtitleAdded);
    sock.on('error', onError);
    return () => {
      sock.off('room:state', onState);
      sock.off('room:members', onMembers);
      sock.off('room:host-changed', onHostChanged);
      sock.off('room:kicked', onKicked);
      sock.off('room:subtitle-added', onSubtitleAdded);
      sock.off('error', onError);
    };
  }, [room.id, toast, currentRole, room, mediaDetail, joinCode, hostKey, setRoom, clearRoom]);

  // WHY: flush any custom subtitle tracks that arrived before the player was
  // ready. Runs when `player` transitions from null to a live Player instance.
  useEffect(() => {
    if (player && pendingSubsRef.current.length > 0) {
      for (const sub of pendingSubsRef.current) {
        addSubtitleTrack(player, mediaDetail.id, sub);
      }
      pendingSubsRef.current = [];
    }
  }, [player, mediaDetail.id]);

  // WHY: sync isFsChat when the user exits browser fullscreen via Esc/F11,
  // so the CSS class and button label stay consistent.
  useEffect(() => {
    const onFsChange = () => {
      const fsEl = document.fullscreenElement;
      if (!fsEl || fsEl !== roomRef.current) {
        setIsFsChat(false);
        setChatCollapsed(false);
      }
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  /**
   * Toggle fullscreen+chat mode: request browser fullscreen on the room
   * container so the whole layout (video + floating chat sidebar) fills the
   * screen. Exiting via Esc/F11 is handled by the fullscreenchange listener.
   */
  const toggleFsChat = () => {
    const el = roomRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      const req = el.requestFullscreen();
      void req.then(() => {
        setIsFsChat(true);
      }).catch((err: unknown) => {
        logger.warn('Failed to enter fullscreen', err);
        toast.error('Failed to enter fullscreen.');
      });
    } else {
      void document.exitFullscreen().then(() => {
        setIsFsChat(false);
        setChatCollapsed(false);
      });
    }
  };

  /**
   * Add a locally-uploaded subtitle (viewer-only) to the player as a blob-URL
   * text track. No server round-trip — the file never leaves the viewer's
   * browser.
   */
  const handleLocalSubtitle = (label: string, blobUrl: string) => {
    const p = playerHandle.current?.player();
    if (!p) {
      toast.error('Player is not ready yet.');
      return;
    }
    try {
      (p as unknown as {
        addRemoteTextTrack(o: {
          kind: string;
          src: string;
          label?: string;
          language?: string;
        }): unknown;
      }).addRemoteTextTrack({
        kind: 'subtitles',
        src: blobUrl,
        label,
      });
    } catch (err) {
      logger.warn('Failed to add local subtitle track', err);
      toast.error('Failed to add subtitle track.');
    }
  };

  // Bind the sync engine. Called unconditionally (Rules of Hooks); player is
  // null until the player reports ready, which the hook tolerates.
  const sync = useRoomSync({ roomId: room.id, player, role: currentRole });
  useMediaCookie({ roomId: room.id, player, onRecovered: sync.resync });

  // Click handler for the autoplay-block overlay: retry play and clear the flag.
  const handleJoinPlayback = () => {
    sync.acknowledgeGesture();
    const p = playerHandle.current?.player();
    if (p) {
      // WHY: play() rejects with NotAllowedError without a user gesture; the
      // overlay click is that gesture, so this retry should succeed.
      const pr = p.play();
      if (pr) {
        void pr.catch((e: unknown) => {
          logger.warn('retry play failed', e);
        });
      }
    }
  };

  const statusEl = (
    <div className={styles.status}>
      <ConnectionStatus connected={sync.connected} drift={sync.drift} />
      <button
        type="button"
        className={styles.fsChatBtn}
        onClick={toggleFsChat}
        title={isFsChat ? 'Exit fullscreen' : 'Fullscreen + Chat'}
        aria-label={isFsChat ? 'Exit fullscreen' : 'Fullscreen + Chat'}
      >
        {isFsChat ? '⤡' : '⤢'}
      </button>
    </div>
  );

  // Layout: slim top bar (brand, title, sync status) over a theater row of
  // stage + chat sidebar. In fullscreen+chat the bar is hidden and the status
  // pill floats over the video instead.
  return (
    <div ref={roomRef} className={`${styles.room} ${isFsChat ? styles.roomFs : ''}`}>
      {isFsChat ? null : (
        <header className={styles.topbar}>
          <Wordmark size="sm" />
          <div className={styles.nowPlaying}>
            <span className={styles.nowPlayingLabel}>Now watching</span>
            <span className={styles.nowPlayingTitle} title={mediaDetail.title}>
              {mediaDetail.title}
            </span>
          </div>
          <span className="spacer" />
          {statusEl}
        </header>
      )}
      <div className={styles.body}>
        <div className={styles.stage}>
          <VideoPlayer
            ref={playerHandle}
            media={mediaDetail}
            role={role}
            fill={isFsChat}
            onReady={(p: Player) => setPlayer(p)}
          />
          {sync.needsUserGesture ? <JoinPlaybackOverlay visible={sync.needsUserGesture} onJoin={handleJoinPlayback} /> : null}
          {isFsChat ? statusEl : null}
        </div>
        <RoomSidebar
          room={room}
          members={members}
          role={currentRole}
          joinCode={joinCode}
          hostKey={hostKey}
          roomId={room.id}
          onLocalSubtitle={handleLocalSubtitle}
          floating={isFsChat}
          collapsed={isFsChat && chatCollapsed}
          onToggleCollapse={() => setChatCollapsed((c) => !c)}
        />
      </div>
    </div>
  );
}

/**
 * RoomPage. Phase 1: ensure session + REST join. Phase 2: render <RoomStage>.
 */
export function RoomPage() {
  const params = useParams<{ roomId: string }>();
  const roomId = params.roomId ?? '';
  const location = useLocation();

  const token = useSessionStore((s) => s.token);
  const room = useSessionStore((s) => s.room);
  const role = useSessionStore((s) => s.role);
  const mediaDetail = useSessionStore((s) => s.mediaDetail);
  const joinCode = useSessionStore((s) => s.joinCode);
  const hostKey = useSessionStore((s) => s.hostKey);
  const setRoom = useSessionStore((s) => s.setRoom);
  const clearRoom = useSessionStore((s) => s.clearRoom);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Ensure we have room/role/media for this room; join via REST if missing
  // (covers direct navigation, page refresh, and post-create detail fetch).
  useEffect(() => {
    if (!roomId) {
      setError('Missing room id.');
      setLoading(false);
      return;
    }
    if (!token) {
      setError('Not authenticated.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    async function ensure(id: string, currentRoomId: string | undefined, hasMedia: boolean) {
      try {
        // WHY: after creating a room we have the summary but no media detail
        // (the create response omits it); joining mints the media cookie and
        // fetches the full MediaDetail. Also join on a room-id mismatch.
        if (!hasMedia || currentRoomId !== id) {
          const res = await postJoinRoom(id, {});
          if (cancelled) return;
          setRoom({
            room: res.room,
            role: res.role,
            mediaDetail: res.media,
            // WHY: joinCode is only returned at room creation; preserve the
            // host's code across this re-join so the sidebar can show it.
            // Joiners never had one (null stays null).
            joinCode: joinCode ?? null,
            hostKey: hostKey ?? null,
          });
        }
        if (!cancelled) setLoading(false);
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to join room.';
        setError(message);
        setLoading(false);
      }
    }
    void ensure(roomId, room?.id, Boolean(mediaDetail));
    return () => {
      cancelled = true;
    };
    // WHY: depend on roomId/token only; re-running on room identity changes
    // would double-join after setRoom.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, token]);

  // Clear the active room from the session on unmount.
  useEffect(() => () => clearRoom(), [clearRoom]);

  // WHY: if not authenticated, redirect to lobby with the current path as
  // returnTo state so the lobby can navigate back after session creation.
  if (!token) return <Navigate to="/" replace state={{ returnTo: location.pathname }} />;

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner label="Joining room…" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>
        <Wordmark size="lg" to={null} />
        <h1 className={styles.errorTitle}>We couldn’t open that room</h1>
        <p className={styles.errorText}>{error}</p>
        <a className={styles.backBtn} href="/">
          Back to join screen
        </a>
      </div>
    );
  }

  if (!mediaDetail || !role || !room) {
    return (
      <div className={styles.error}>
        <Wordmark size="lg" to={null} />
        <h1 className={styles.errorTitle}>We couldn’t open that room</h1>
        <p className={styles.errorText}>Room data unavailable.</p>
        <a className={styles.backBtn} href="/">
          Back to join screen
        </a>
      </div>
    );
  }

  return <RoomStage room={room} role={role} mediaDetail={mediaDetail} joinCode={joinCode} hostKey={hostKey} />;
}
